import lodash from "lodash";
import { v7 as uuidv7 } from "uuid";

import {
	BOARDS_KEY_MAPPER_ENUM,
	OPERATION_COLUMNS,
	OPERATION_ITEMS_COLUMNS,
	USERS_COLUMNS,
} from "@onesolution/constants";
import type { PriceForOpCreation } from "@onesolution/serverFn";
import type { UserMutationVariables } from "@onesolution/serverFn/user";
import type { OperationMutationVariables } from "@onesolution/utils/operationValidator";
import { statusColorsToValues } from "@onesolution/utils/status";

const DAY_IN_MS = 1000 * 60 * 60 * 24;

export function formatColumnsValues(fields: object, removeEmpty = false) {
	const withoutEmptyKeys = lodash.omitBy(fields, lodash.isNil);
	const preformatted = removeEmpty
		? lodash.omitBy(fields, lodash.isEmpty)
		: withoutEmptyKeys;

	return preformatted;
}

export function formatLinkField(value: string | string[] | null | undefined) {
	if (!value) return { item_ids: [] };
	if (Array.isArray(value)) return { item_ids: value };

	return { item_ids: [value] };
}

export function formatCheckboxField(value: boolean) {
	return { checked: value.toString() };
}

export function formatOperationItemsForMutation(
	parentItemId: string,
	item: PriceForOpCreation,
	deliveryDate?: string | null,
	isNew?: boolean | null,
) {
	const out = {
		parentItemId,
		itemName: item.name,
		columnValues: JSON.stringify(
			formatColumnsValues(
				{
					[OPERATION_ITEMS_COLUMNS.channel]: item.channel.label,
					[OPERATION_ITEMS_COLUMNS.job]: item.jobs.map((j) => j.id).join(","),
					[OPERATION_ITEMS_COLUMNS.prices]: item.prices,
					[OPERATION_ITEMS_COLUMNS.status]: isNew ? "NEW" : undefined,
					[OPERATION_ITEMS_COLUMNS.timeline]: deliveryDate
						? deliveryDate?.split("T")[0]
						: undefined,
					[OPERATION_ITEMS_COLUMNS.urgent]:
						deliveryDate &&
						(new Date(deliveryDate).getTime() - new Date().getTime()) /
							DAY_IN_MS <=
							2 // delivery date is within 2 days from now
							? "URGENT"
							: undefined,
					[OPERATION_ITEMS_COLUMNS.workflowSteps]: item.workflowSteps
						.map((s) => s.label)
						.join(","),
				},
				true,
			),
		),
	};

	return out;
}

export function formatOperationCreateColumnsForMutation(
	params: OperationMutationVariables,
	creatorId: string,
) {
	return formatColumnsValues({
		[OPERATION_COLUMNS.name]: params.name,
		[OPERATION_COLUMNS.briefId]: `brief_${uuidv7()}`,
		[OPERATION_COLUMNS.creationDate]: creatorId
			? new Date().toISOString().split("T")[0]
			: null,
		[OPERATION_COLUMNS.creator]: creatorId ? formatLinkField(creatorId) : null,
		[OPERATION_COLUMNS.assignees]: creatorId
			? formatLinkField(creatorId)
			: null,
	});
}

export async function formatOperationUpdateColumnsForMutation(
	params: OperationMutationVariables,
) {
	return formatColumnsValues({
		[OPERATION_COLUMNS.name]: params.name,
		[OPERATION_COLUMNS.status]: (
			await statusColorsToValues(
				BOARDS_KEY_MAPPER_ENUM.briefs,
				OPERATION_COLUMNS.status,
				[params.status!],
			)
		)?.[0],
		[OPERATION_COLUMNS.deadline]: params.deadline,
		[OPERATION_COLUMNS.timelineOriginal]: params.timeline,
		[OPERATION_COLUMNS.description]: params.description,
		[OPERATION_COLUMNS.entities]: params.entities?.join(","),
		[OPERATION_COLUMNS.campaigns]: formatLinkField(params.campaigns),
		[OPERATION_COLUMNS.services]: formatLinkField(params.services),
		[OPERATION_COLUMNS.marketingPlans]: formatLinkField(params.marketingPlans),
	});
}

export function formatUserColumnsForMutation(params: UserMutationVariables) {
	return formatColumnsValues({
		[USERS_COLUMNS.firstname]: params.firstname,
		[USERS_COLUMNS.lastname]: params.lastname,
		[USERS_COLUMNS.active]: formatCheckboxField(params.active),
		[USERS_COLUMNS.email]: params.email,
		[USERS_COLUMNS.language]: params.language,
		[USERS_COLUMNS.role]: params.role,
		[USERS_COLUMNS.services]: formatLinkField(params.services),
		[USERS_COLUMNS.favoriteServices]: formatLinkField(params.favoriteServices),
	});
}

export const formatPaginatedResult =
	<T>(cursorKey: string) =>
	async (rawData: unknown, formattedData: unknown) => {
		const cursor = lodash.get(rawData, cursorKey)! as string;
		const items = formattedData as T[];

		return {
			cursor,
			items,
		};
	};
